import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Chip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AdminPanelSettings as AdminIcon,
  RssFeed as FeedIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Admin Viewer', icon: <AdminIcon />, path: '/admin' },
    { text: 'Trusted Feed', icon: <FeedIcon />, path: '/feed' },
  ];

  const isActive = (path) => {
    if (path === '/feed') {
      return location.pathname.startsWith('/feed');
    }
    return location.pathname === path;
  };

  const drawer = (
    <Box sx={{ width: drawerOpen ? 280 : 70, height: '100%' }}>
      {/* Drawer Header */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'relative'
        }}
      >
        {drawerOpen && (
          <>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)' }}>
              <DashboardIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                GBP Database
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {user?.username}
              </Typography>
            </Box>
          </>
        )}
        <IconButton
          onClick={handleDrawerToggle}
          sx={{ 
            position: 'absolute', 
            right: 8, 
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          {drawerOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>

      {/* Navigation Menu */}
      <List sx={{ pt: 2 }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.text}
            button
            onClick={() => {
              navigate(item.path);
            }}
            sx={{
              mx: 2,
              mb: 1,
              borderRadius: 2,
              backgroundColor: isActive(item.path) ? 'primary.main' : 'transparent',
              color: isActive(item.path) ? 'white' : '#1a202c',
              '&:hover': {
                backgroundColor: isActive(item.path) ? 'primary.dark' : 'rgba(102, 126, 234, 0.08)',
              },
              justifyContent: drawerOpen ? 'flex-start' : 'center',
              minHeight: 48
            }}
          >
            <ListItemIcon sx={{ 
              color: isActive(item.path) ? 'white' : 'inherit',
              minWidth: drawerOpen ? 40 : 'auto'
            }}>
              {item.icon}
            </ListItemIcon>
            {drawerOpen && (
              <>
                <ListItemText 
                  primary={item.text}
                  sx={{ 
                    '& .MuiListItemText-primary': {
                      fontWeight: isActive(item.path) ? 'bold' : 'normal'
                    }
                  }}
                />
                {isActive(item.path) && (
                  <Chip 
                    label="Active" 
                    size="small" 
                    color="secondary"
                    sx={{ ml: 'auto' }}
                  />
                )}
              </>
            )}
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      {/* User Section */}
      <List>
        <ListItem
          button
          onClick={handleProfileMenuOpen}
          sx={{
            mx: 2,
            mb: 1,
            borderRadius: 2,
            color: '#1a202c',
            '&:hover': {
              backgroundColor: 'rgba(102, 126, 234, 0.08)',
            },
            justifyContent: drawerOpen ? 'flex-start' : 'center',
            minHeight: 48
          }}
        >
          <ListItemIcon sx={{ 
            minWidth: drawerOpen ? 40 : 'auto'
          }}>
            <AccountCircleIcon />
          </ListItemIcon>
          {drawerOpen && (
            <ListItemText 
              primary="Account"
              secondary={user?.role === 'admin' ? 'Administrator' : 'User'}
            />
          )}
        </ListItem>
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            mx: 2,
            borderRadius: 2,
            color: '#1a202c',
            '&:hover': {
              backgroundColor: 'rgba(244, 67, 54, 0.08)',
              color: 'error.main',
            },
            justifyContent: drawerOpen ? 'flex-start' : 'center',
            minHeight: 48
          }}
        >
          <ListItemIcon sx={{ 
            minWidth: drawerOpen ? 40 : 'auto'
          }}>
            <LogoutIcon />
          </ListItemIcon>
          {drawerOpen && <ListItemText primary="Logout" />}
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Mobile Menu Button - Only visible on mobile */}
      <Box
        sx={{
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 1200,
          display: { xs: 'block', sm: 'none' }
        }}
      >
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            color: '#1a202c',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.98)',
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerOpen ? 280 : 70,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderTop: 'none',
            transition: 'width 0.3s ease'
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: 8, sm: 4 }, // Reduced top padding since no app bar
          pl: { xs: 0, sm: drawerOpen ? '280px' : '70px' }, // Adjust based on drawer state
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          transition: 'padding-left 0.3s ease',
          overflow: 'auto',
          height: '100vh'
        }}
      >
        {children}
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          className: 'modern-card',
          sx: {
            mt: 1,
            minWidth: 200,
          }
        }}
      >
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={handleProfileMenuClose}>
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout; 